import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  getIdTokenResult,
} from "firebase/auth";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import app, { auth, db } from "../config/firebase";
import "./AdminBanners.css";

/* =========================================================
   HELPERS
========================================================= */
const safeDateToInput = (tsOrDate) => {
  try {
    const d =
      tsOrDate?.toDate?.() ||
      (tsOrDate instanceof Date ? tsOrDate : tsOrDate ? new Date(tsOrDate) : null);
    if (!d || Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

const bannerImg = (b) => b?.imageUrl || b?.bannerImageUrl || "";

/* =========================================================
   COMPONENT (ENXUTO / ESTÁVEL)
   - removeu: editar posição
   - manteve: aprovar/recusar, excluir, +7d, bloquear/desbloquear empresas
========================================================= */
export default function AdminBanners() {
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  /* ================= AUTH ================= */
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminClaims, setAdminClaims] = useState({});

  /* ================= LOGIN ================= */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  /* ================= UI ================= */
  const [tab, setTab] = useState("banners"); // "banners" | "companies"
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busyId, setBusyId] = useState("");

  /* ================= DATA ================= */
  const [companies, setCompanies] = useState([]);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);

  const [pendingAdRequests, setPendingAdRequests] = useState([]);
  const [banners, setBanners] = useState([]);

  /* ================= FUNCTIONS ================= */
  const functions = useMemo(() => getFunctions(app, "us-central1"), []);

  const callApproveAd = useMemo(() => httpsCallable(functions, "approveAdRequest"), [functions]);
  const callRejectAd  = useMemo(() => httpsCallable(functions, "rejectAdRequest"), [functions]);
  const callExtend    = useMemo(() => httpsCallable(functions, "extendBanner"), [functions]);
  const callDeleteBan = useMemo(() => httpsCallable(functions, "deleteBanner"), [functions]);

  const callToggleCompany = useMemo(() => httpsCallable(functions, "toggleCompanyBlock"), [functions]);
  const callDeleteCompany = useMemo(() => httpsCallable(functions, "deleteCompany"), [functions]);

  /* ================= ADMIN CHECK ================= */
  const computeIsAdminFromClaims = (claims) => !!claims?.admin || claims?.role === "admin";

  useEffect(() => {
    mountedRef.current = true;

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!mountedRef.current) return;

      setUser(u || null);

      if (!u) {
        setAdminClaims({});
        setIsAdmin(false);
        setAuthReady(true);
        return;
      }

      try {
        const token = await getIdTokenResult(u, true);
        setAdminClaims(token?.claims || {});
        setIsAdmin(computeIsAdminFromClaims(token?.claims || {}));
      } catch {
        setAdminClaims({});
        setIsAdmin(false);
      } finally {
        setAuthReady(true);
      }
    });

    return () => {
      mountedRef.current = false;
      unsub();
    };
  }, []);

  /* ================= LOGIN ACTIONS ================= */
  const doLogin = async () => {
    if (loginBusy) return;
    setError("");
    setOk("");
    setLoginBusy(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
      const token = await getIdTokenResult(cred.user, true);
      const adminOk = computeIsAdminFromClaims(token?.claims || {});

      setAdminClaims(token?.claims || {});
      setIsAdmin(adminOk);
      setPassword("");

      if (!adminOk) setError("Acesso negado: sua conta não tem permissão de admin.");
      else setOk("Login efetuado!");
    } catch (e) {
      console.error("LOGIN ERROR:", e?.code, e?.message);
      setError("Login inválido. Verifique e-mail e senha.");
    } finally {
      setLoginBusy(false);
    }
  };

  const doLogout = async () => {
    await signOut(auth).catch(() => null);
  };

  /* ================= SUBSCRIPTIONS (ADMIN) ================= */
  useEffect(() => {
    if (!isAdmin) return;

    setError("");
    setOk("");

    // companies
    const unsubCompanies = onSnapshot(
      query(collection(db, "companies"), orderBy("createdAt", "desc")),
      (snap) => setCompanies(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("ERRO companies:", err?.code, err?.message);
        setError(
          err?.code === "permission-denied"
            ? "Sem permissão para listar empresas (rules)."
            : "Falha ao carregar empresas."
        );
      }
    );

    // adRequests
    const unsubAd = onSnapshot(
      query(collection(db, "adRequests"), orderBy("createdAt", "desc")),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPendingAdRequests(
          all.filter((r) => String(r.status || "pending") === "pending")
        );
      },
      (err) => {
        console.error("ERRO adRequests:", err?.code, err?.message);
        setError("Falha ao carregar solicitações de anúncios.");
      }
    );

    // banners
    const unsubBanners = onSnapshot(
      query(collection(db, "banners"), orderBy("createdAt", "desc")),
      (snap) => setBanners(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("ERRO banners:", err?.code, err?.message);
        setError("Falha ao carregar banners.");
      }
    );

    return () => {
      try { unsubCompanies(); } catch {}
      try { unsubAd(); } catch {}
      try { unsubBanners(); } catch {}
    };
  }, [isAdmin]);

  /* ================= ACTIONS — ADS ================= */
  const approveAdRequest = async (req) => {
    if (!req?.id) return;

    setBusyId(req.id);
    setError("");
    setOk("");

    try {
      // Mantém exatamente como está funcionando hoje:
      // placement e dias vêm do request (admin aprova ou recusa)
      await callApproveAd({
        requestId: req.id,
        placement: String(req.placement || req.targetPlacement || "side").toLowerCase(),
        days: Number(req.days || req.planDays || 30),
      });

      setOk("Solicitação aprovada!");
    } catch (e) {
      console.error("APPROVE ERROR:", e?.code, e?.message);
      setError(`Erro ao aprovar: ${e?.message || e?.code || "internal"}`);
    } finally {
      setBusyId("");
    }
  };

  const rejectAdRequest = async (req) => {
    if (!req?.id) return;

    setBusyId(req.id);
    setError("");
    setOk("");

    try {
      await callRejectAd({ requestId: req.id });
      setOk("Solicitação recusada.");
    } catch (e) {
      console.error("REJECT ERROR:", e?.code, e?.message);
      setError(`Erro ao recusar: ${e?.message || e?.code || "internal"}`);
    } finally {
      setBusyId("");
    }
  };

  /* ================= ACTIONS — BANNERS ================= */
  const extendBannerDays = async (banner, days) => {
    if (!banner?.id) return;

    setBusyId(banner.id);
    setError("");
    setOk("");

    try {
      await callExtend({ bannerId: banner.id, mode: "addDays", days: Number(days) });
      setOk("Prazo prorrogado!");
    } catch (e) {
      console.error("EXTEND ERROR:", e?.code, e?.message);
      setError(`Falha ao prorrogar: ${e?.message || e?.code || "internal"}`);
    } finally {
      setBusyId("");
    }
  };

  const deleteBanner = async (banner) => {
    if (!banner?.id) return;

    const nm = banner.companyName || "empresa";
    if (!window.confirm(`Excluir permanentemente o anúncio de "${nm}"?`)) return;

    setBusyId(banner.id);
    setError("");
    setOk("");

    try {
      const res = await callDeleteBan({ bannerId: banner.id });
      if (res?.data?.ok) setOk("Anúncio removido com sucesso!");
      else setOk("Exclusão solicitada.");
    } catch (e) {
      console.error("DELETE BANNER ERROR:", e?.code, e?.message);
      setError(`Erro ao excluir anúncio: ${e?.message || e?.code || "internal"}`);
    } finally {
      setBusyId("");
    }
  };

  /* ================= ACTIONS — COMPANIES ================= */
  const toggleCompanyActive = async (company) => {
    if (!company?.id) return;

    setBusyId(company.id);
    setError("");
    setOk("");

    // seu doc usa "active: true/false"
    const nextActive = !Boolean(company.active);

    try {
      // backend aceita active e também blocked por compatibilidade
      await callToggleCompany({
        companyId: company.id,
        active: nextActive,
        blocked: !nextActive,
      });

      setOk(nextActive ? "Empresa desbloqueada!" : "Empresa bloqueada!");
    } catch (e) {
      console.error("TOGGLE COMPANY ERROR:", e?.code, e?.message);
      setError(`Erro ao alterar status da empresa: ${e?.message || e?.code || "internal"}`);
    } finally {
      setBusyId("");
    }
  };

  const removeCompany = async (company) => {
    if (!company?.id) return;

    const nm = company.companyName || company.name || "empresa";
    if (!window.confirm(`Excluir permanentemente "${nm}"?`)) return;

    setBusyId(company.id);
    setError("");
    setOk("");

    try {
      await callDeleteCompany({ companyId: company.id });
      setOk("Empresa excluída!");
      if (expandedCompanyId === company.id) setExpandedCompanyId(null);
    } catch (e) {
      console.error("DELETE COMPANY ERROR:", e?.code, e?.message);
      setError(`Erro ao excluir empresa: ${e?.message || e?.code || "internal"}`);
    } finally {
      setBusyId("");
    }
  };

  /* ================= GUARDS ================= */
  if (!authReady) {
    return (
      <div className="adm-page-root">
        <div className="adm-content-area">Carregando...</div>
      </div>
    );
  }

  // LOGIN
  if (!user) {
    return (
      <div className="adm-page-root">
        <div className="adm-content-area" style={{ maxWidth: 520 }}>
          <div className="adm-form-card">
            <h2 style={{ marginTop: 0 }}>Login Administrativo</h2>

            {error && <div style={{ marginTop: 10, color: "#991b1b", fontWeight: 800 }}>{error}</div>}
            {ok && <div style={{ marginTop: 10, color: "#065f46", fontWeight: 800 }}>{ok}</div>}

            <div className="adm-form-grid" style={{ gridTemplateColumns: "1fr" }}>
              <div className="adm-form-group">
                <label>E-mail</label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@..."
                  onKeyDown={(e) => e.key === "Enter" && doLogin()}
                />
              </div>

              <div className="adm-form-group">
                <label>Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  onKeyDown={(e) => e.key === "Enter" && doLogin()}
                />
              </div>
            </div>

            <div className="adm-form-actions">
              <button className="adm-btn-submit" onClick={doLogin} disabled={loginBusy}>
                {loginBusy ? "Entrando..." : "Entrar"}
              </button>

              <button
                className="adm-sidebar-link"
                style={{ width: "auto" }}
                onClick={() => navigate("/")}
                disabled={loginBusy}
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // NOT ADMIN
  if (!isAdmin) {
    return (
      <div className="adm-page-root">
        <div className="adm-content-area">
          <div className="adm-form-card">
            <h2 style={{ marginTop: 0 }}>Acesso negado</h2>
            <p>Você não tem privilégios de administrador.</p>
            <p style={{ fontSize: 12, opacity: 0.85 }}>claims atuais: {JSON.stringify(adminClaims || {})}</p>
            <button className="adm-btn-submit" onClick={doLogout}>Sair</button>
          </div>
        </div>
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="adm-page-root">
      {/* SIDEBAR */}
      <aside className="adm-sidebar">
        <div
          className="adm-sidebar-brand"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          ACHE<span>VAM</span>
        </div>

        <button
          className={`adm-sidebar-link ${tab === "banners" ? "active" : ""}`}
          onClick={() => setTab("banners")}
        >
          Anúncios
        </button>

        <button
          className={`adm-sidebar-link ${tab === "companies" ? "active" : ""}`}
          onClick={() => setTab("companies")}
        >
          Empresas
        </button>

        <button className="adm-sidebar-link adm-exit" onClick={doLogout}>
          Sair
        </button>
      </aside>

      {/* CONTENT */}
      

      <main className="adm-content-area">
        {error && (
          <div className="adm-form-card" style={{ borderLeft: "6px solid #ef4444", color: "#991b1b" }}>
            {error}
          </div>
        )}
        {ok && (
          <div className="adm-form-card" style={{ borderLeft: "6px solid #10b981", color: "#065f46" }}>
            {ok}
          </div>
        )}

       
        <div className="adm-topbar">
  <div className="adm-topbar-left">
    <div className="adm-topbar-title">Painel Administrativo</div>
    <div className="adm-user-pill">Logado: {user?.email}</div>
  </div>

  <div className="adm-topbar-right">
    <button className="adm-topbar-exit" onClick={doLogout}>Sair</button>
  </div>
</div>
        {/* ================= TAB: BANNERS ================= */}
        {tab === "banners" && (
          <>
            <h2 style={{ marginTop: 0 }}>Solicitações de Anúncio</h2>

            <div className="adm-form-card">
              {pendingAdRequests.length === 0 ? (
                <p style={{ margin: 0 }}>Tudo em dia! Sem solicitações pendentes.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {pendingAdRequests.map((r) => {
                    const img = r.bannerImageUrl || r.imageUrl || "";
                    return (
                      <div key={r.id} className="route-item" style={{ alignItems: "center" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 900 }}>{r.companyName || "Empresa"}</div>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>
                            Plano: {r.plan || "—"} • Duração: {Number(r.days || r.planDays || 30)} dias
                          </div>

                          {!!img && (
                            <div style={{ marginTop: 8 }}>
                              <img
                                src={img}
                                alt="banner"
                                style={{
                                  width: "100%",
                                  maxWidth: 320,
                                  borderRadius: 10,
                                  border: "1px solid #e2e8f0",
                                }}
                              />
                            </div>
                          )}
                        </div>

                        <div style={{ display: "flex", gap: 10 }}>
                          <button
                            className="adm-btn-small ok"
                            disabled={busyId === r.id}
                            onClick={() => approveAdRequest(r)}
                          >
                            {busyId === r.id ? "..." : "Aprovar"}
                          </button>
                          <button
                            className="adm-btn-small no"
                            disabled={busyId === r.id}
                            onClick={() => rejectAdRequest(r)}
                          >
                            Recusar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <h2 style={{ marginTop: 26 }}>Anúncios Ativos</h2>

            <div className="adm-form-card">
              {banners.length === 0 ? (
                <p style={{ margin: 0 }}>Nenhum anúncio cadastrado.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {banners.map((b) => (
                    <div key={b.id} className="route-item">
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 900 }}>{b.companyName || "Empresa"}</div>
                        <div style={{ fontSize: 12, opacity: 0.85 }}>
                          Expira: {safeDateToInput(b.expiresAt) || "—"}
                        </div>

                        {!!bannerImg(b) && (
                          <img
                            src={bannerImg(b)}
                            alt="preview"
                            style={{
                              width: "100%",
                              maxWidth: 320,
                              marginTop: 10,
                              borderRadius: 10,
                              border: "1px solid #e2e8f0",
                            }}
                          />
                        )}
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <button
                          className="opt-btn"
                          disabled={busyId === b.id}
                          onClick={() => extendBannerDays(b, 7)}
                        >
                          {busyId === b.id ? "..." : "Prorrogar +7d"}
                        </button>

                        <button
                          className="opt-btn"
                          style={{ color: "red" }}
                          disabled={busyId === b.id}
                          onClick={() => deleteBanner(b)}
                        >
                          {busyId === b.id ? "Excluindo..." : "Excluir anúncio"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ================= TAB: COMPANIES ================= */}
        {tab === "companies" && (
          <>
            <h2 style={{ marginTop: 0 }}>Gestão de Empresas</h2>

            <div className="company-list">
              {companies.map((c) => {
                const name = c.companyName || c.name || "(sem nome)";
                const isExpanded = expandedCompanyId === c.id;
                const isActive = Boolean(c.active);

                return (
                  <div key={c.id} className={`company-card ${!isActive ? "blocked" : ""}`}>
                    <div
                      className="company-header"
                      onClick={() => setExpandedCompanyId(isExpanded ? null : c.id)}
                    >
                      <div className="company-info-basic">
                        <img
                          src={c.logoUrl || "/placeholder-logo.png"}
                          alt="Logo"
                          className="mini-logo"
                        />
                        <div>
                          <strong>{name}</strong>
                          <span>{c.category || "—"} • {c.city || "—"}</span>
                        </div>
                      </div>
                      <div className="badge">{isActive ? "Ativo" : "Bloqueado"}</div>
                    </div>

                    {isExpanded && (
                      <div className="company-details animate-in">
                        <hr />

                        <div className="details-grid">
                          <div className="detail-item"><strong>Status:</strong> {String(c.status || "—")}</div>
                          <div className="detail-item"><strong>Ativo:</strong> {Boolean(c.active) ? "sim" : "não"}</div>

                          <div className="detail-item"><strong>Empresa:</strong> {c.companyName || c.name || "—"}</div>
                          <div className="detail-item"><strong>Responsável:</strong> {c.responsibleName || "—"}</div>

                          <div className="detail-item"><strong>E-mail:</strong> {c.email || "—"}</div>
                          <div className="detail-item"><strong>Telefone/WhatsApp:</strong> {c.phone || c.whatsapp || "—"}</div>

                          <div className="detail-item"><strong>CNPJ:</strong> {c.cnpj || "—"}</div>
                          <div className="detail-item"><strong>CPF:</strong> {c.cpf || "—"}</div>

                          <div className="detail-item"><strong>Qtd. carros:</strong> {c.fleetSize || "—"}</div>
                          <div className="detail-item"><strong>Qtd. rotas liberadas:</strong> {c.routeCount || "—"}</div>

                          <div className="detail-item" style={{ gridColumn: "1 / -1" }}><strong>Endereço:</strong> {c.address || "—"}</div>
                          <div className="detail-item"><strong>Criado em:</strong> {safeDateToInput(c.createdAt) || "—"}</div>
                        </div>

                        <div className="admin-actions">
                          <button
                            className={`btn-action ${isActive ? "block" : "unblock"}`}
                            disabled={busyId === c.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCompanyActive(c);
                            }}
                          >
                            {busyId === c.id
                              ? "Aguarde..."
                              : isActive
                              ? "Bloquear Empresa"
                              : "Desbloquear"}
                          </button>

                          <button
                            className="btn-action delete"
                            disabled={busyId === c.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCompany(c);
                            }}
                          >
                            {busyId === c.id ? "Aguarde..." : "Excluir Registro"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
